import React, { useCallback, useEffect, useState } from 'react';
import { Check, KeyRound, Mic2, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import {
  StaffError,
  staffRequest,
} from '../reflect/staff/ReflectStaffShell';
import { StaffPanel, StaffSwitch } from '../reflect/staff/StaffControls';

const KEY_LABELS = {
  turnstile: 'Turnstile',
  hash_secret: 'Hash secret',
  anthropic: 'Anthropic',
};

const dollars = (cents) => `$${(Number(cents || 0) / 100).toFixed(2)}`;

const BoothAdminCard = () => {
  const [stats, setStats] = useState(null);
  const [cap, setCap] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await staffRequest('/api/admin/booth/stats');
      setStats(data);
      setCap((Number(data.cap_cents || 0) / 100).toFixed(2));
    } catch (requestError) {
      setError(requestError.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = async (body) => {
    setBusy(true);
    setError('');
    try {
      const data = await staffRequest('/api/admin/booth/settings', {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      setStats(data);
      setCap((Number(data.cap_cents || 0) / 100).toFixed(2));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const spent = Number(stats?.spent_cents || 0);
  const capCents = Number(stats?.cap_cents || 0);
  const percent = capCents > 0 ? Math.min(100, (spent / capCents) * 100) : 0;
  const outcomes = stats?.outcomes || {};
  const keysReady = stats?.keys_ready || {};
  const allKeysReady = Object.keys(KEY_LABELS).every((key) => keysReady[key]);

  return (
    <StaffPanel className="mb-6 border-cyan/25 bg-cyan/[0.025]">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-cyan/10 text-cyan">
            <Mic2 className="h-5 w-5" />
          </span>
          <div>
            <h2 className="font-heading text-2xl font-black">The Booth</h2>
            <p className="text-sm text-white/50">AI usage and monthly limits.</p>
          </div>
        </div>
        <Link
          to="/booth"
          className="flex min-h-11 items-center rounded-full border border-cyan/40 px-4
            text-sm font-black text-cyan hover:bg-cyan/10"
        >
          Open The Booth
        </Link>
      </div>

      <StaffError>{error}</StaffError>

      {!stats ? (
        <div className="mt-5 h-28 animate-pulse rounded-2xl bg-white/[0.05]" />
      ) : (
        <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1fr)_20rem]">
          <div>
            <div className="flex items-end justify-between gap-4">
              <div>
                <p className="text-sm font-black uppercase tracking-[0.16em] text-white/40">
                  This month
                </p>
                <p className="mt-1 font-heading text-3xl font-black">
                  {dollars(spent)} of {dollars(capCents)}
                </p>
              </div>
              <p className="text-sm font-bold text-white/45">{Math.round(percent)}%</p>
            </div>
            <div className="mt-3 h-3 overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full rounded-full bg-cyan shadow-[0_0_14px_rgba(0,255,221,0.65)]"
                style={{ width: `${percent}%` }}
              />
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
              <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                <p className="text-sm text-white/45">Moves today</p>
                <p className="mt-1 font-heading text-2xl font-black">{stats.moves_today}</p>
              </div>
              <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                <p className="text-sm text-white/45">Moves this month</p>
                <p className="mt-1 font-heading text-2xl font-black">{stats.moves_this_month}</p>
              </div>
              <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                <p className="text-sm text-white/45">Redirects</p>
                <p className="mt-1 font-heading text-2xl font-black">{outcomes.redirect || 0}</p>
              </div>
              <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                <p className="text-sm text-white/45">Blocked</p>
                <p className="mt-1 font-heading text-2xl font-black">{outcomes.blocked || 0}</p>
              </div>
              <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                <p className="text-sm text-white/45">Care screens</p>
                <p className="mt-1 font-heading text-2xl font-black">{outcomes.care || 0}</p>
              </div>
              {stats.uncertain_moves > 0 && (
                <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                  <p className="text-sm text-white/45">Uncertain moves</p>
                  <p className="mt-1 font-heading text-2xl font-black">{stats.uncertain_moves}</p>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-5 rounded-2xl border border-white/10 bg-black/20 p-4">
            <StaffSwitch
              checked={Boolean(stats.ai_on)}
              disabled={busy}
              onChange={(aiOn) => update({ ai_on: aiOn })}
              label="AI moves"
              description={stats.ai_available ? 'Available in The Booth.' : 'Currently unavailable.'}
            />

            <form
              onSubmit={(event) => {
                event.preventDefault();
                const value = Number(cap);
                if (Number.isFinite(value) && value >= 0 && value <= 200) {
                  update({ monthly_cap_dollars: value });
                }
              }}
              className="border-t border-white/10 pt-4"
            >
              <label htmlFor="booth-monthly-cap" className="text-base font-black">
                Monthly cap in dollars
              </label>
              <div className="mt-2 flex gap-2">
                <input
                  id="booth-monthly-cap"
                  type="number"
                  min="0"
                  max="200"
                  step="0.01"
                  value={cap}
                  onChange={(event) => setCap(event.target.value)}
                  className="min-h-11 min-w-0 flex-1 rounded-xl border border-white/15
                    bg-black/30 px-3 text-base text-white outline-none focus:border-cyan"
                />
                <Button
                  type="submit"
                  disabled={busy}
                  className="min-h-11 rounded-full bg-cyan px-4 font-black text-black"
                >
                  Save
                </Button>
              </div>
            </form>

            <div className="border-t border-white/10 pt-4">
              <p className="flex items-center gap-2 font-heading text-lg font-black">
                <KeyRound className="h-5 w-5 text-cyan" /> Keys
              </p>
              <div className="mt-3 space-y-2">
                {Object.entries(KEY_LABELS).map(([key, label]) => (
                  <p key={key} className="flex min-h-11 items-center justify-between gap-3
                    rounded-xl border border-white/10 px-3 text-sm font-bold"
                  >
                    {label}
                    <span className="flex items-center gap-1.5 text-white/55">
                      {keysReady[key]
                        ? <Check className="h-4 w-4 text-cyan" />
                        : <X className="h-4 w-4" />}
                      {keysReady[key] ? 'Ready' : 'Missing'}
                    </span>
                  </p>
                ))}
              </div>
              {!allKeysReady && (
                <p className="mt-3 text-sm font-bold text-white/55">
                  AI moves stay off until every key is set.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </StaffPanel>
  );
};

export default BoothAdminCard;
